import { createMemo, Match, Switch, type Accessor } from 'solid-js';
import { resizeObjectCmd } from 'Shared/posters/project/commands';
import { setObjectPositionSize, setDetachedInStore } from 'Shared/posters/project/objects';
import { DrawResizeHandles } from 'Shared/posters/canvas/DrawResizeHandles';
import { applyResizeDelta, applyAspectResizeDelta, isCornerHandle, type ResizeBounds } from 'Shared/posters/canvas/utils';
import type { ObjectContext } from 'Shared/posters/project/objects';
import type { SelectionContext } from 'Shared/posters/canvas/selection';
import type { DragContext } from 'Shared/posters/canvas/drag';
import type { HistoryContext } from 'Shared/posters/project/history';
import { ResizeMode } from 'Shared/posters/canvas/interactions/ResizeMode';
import { type DraggableImpl } from 'Shared/posters/canvas/interactions/ObjectImpl';
import { detachIfLinked, detachmentCommit, linkedBox } from 'Shared/posters/canvas/interactions/linkedLeaf';
import type { DesignLinkContext } from 'Shared/posters/project/designLink';
import type { Mode } from 'Shared/posters/project/PosterMode';
import { useUser } from 'Shared/SiteProvider';
import type { Canvas } from 'Shared/posters/project/posterSchema';
import { TextResizeHandles } from 'Shared/posters/canvas/interactions/TextResizeHandles';
import { cloneBox, sameBox, type Box, type Position } from 'Shared/posters/types';

interface IResizeHandles {
	objects:      ObjectContext;
	selection:    SelectionContext;
	history:      HistoryContext;
	drag:         DragContext;
	impl:         DraggableImpl;
	canvas:       Canvas;
	design:       DesignLinkContext;
	mode:         Mode;
	resizeMode:   ResizeMode;
	parentOffset: (() => Position) | undefined;
}


export function ResizeHandles(P: IResizeHandles)
{
	// The same box SelectableObject draws the object and its selection rect at — the template's laid-out
	// box for a linked user-mode leaf, the impl's own (possibly clamped) box otherwise — so the handles
	// always sit on the box the user can see.
	const box = createMemo(() => linkedBox(P.mode, P.design, P.impl, !useUser()));
//XXX can parentOffset change? Perhaps we don't need the creatememo() here?
	const parentOffset = createMemo(() => P.parentOffset?.() ?? {x:0,y:0});

	const absLeft = () => parentOffset().x + box().pos.x;
	const absTop  = () => parentOffset().y + box().pos.y;

	return (
		<g transform={`translate(${absLeft()}, ${absTop() + P.impl.contentOffsetY()})`}>
			<Switch>
				<Match when={P.impl.type()=='text'}>
					<TextResizeHandles {...P} />
				</Match>
				<Match when={true}>
					<DrawResizeHandles impl={P.impl}
						onHandleDown={(handle,e) =>
							resize(e,P.impl,handle,P.canvas,P.drag,P.objects,P.history,P.design,P.mode,box)
						}
					/>
				</Match>
			</Switch>
		</g>
	);
}

// box is passed as an accessor, not a value: detaching a linked leaf writes the laid-out position into
// the live box, and the seed below has to be the box *after* that write — otherwise the first drag frame
// snaps the object back to its stale stored position.
function resize(e:MouseEvent,impl:DraggableImpl,handle:string,canvas:Canvas,dragCtx:DragContext,objCtx:ObjectContext,histCtx:HistoryContext,design:DesignLinkContext,mode:Mode,box:Accessor<Box>)
{
	e.preventDefault();
	e.stopPropagation();
	// A linked leaf detaches here exactly as it does when a drag starts (see detachIfLinked): while it is
	// still linked the template owns its position, so only the size write would land — dragging the nw
	// handle left the top-left pinned and resized the box from the bottom-right instead.
	const detached = detachIfLinked(impl,design,objCtx,mode);
	const storeBox     = impl.box;
	const effectiveBox = box();
	// The detachment rides on the resize command, so undoing the resize also puts the leaf back under
	// the template's layout — the same one-step rule a move follows (see moveObjectCmd's detachedSlot).
	const commit = detached ? detachmentCommit(histCtx, objCtx, impl.id, detached.slot) : null;
	console.log('[RESIZE-DETACH]', handle, impl.id, 'detached=', !!detached, 'seed=', JSON.stringify(effectiveBox.pos), 'live=', JSON.stringify(storeBox.pos));
	// Clamp against the effective (rendered) position. For a flex-laid-out child the
	// container owns the position, so pos.left/top is a stale design value; using it
	// would cap the size short of the container edge by that stale offset. effLeft/effTop
	// equal pos.left/top for absolute elements, so this is a no-op there.
	// Seed from the *drawn* box: for an auto-grown container effectiveBox.top is anchored per layout
	// and effectiveBox.height is the grown height, while pos.top/height still hold the smaller designed
	// values. Pairing the anchored top with the stale height snapped the (anchor) edge and jumped the
	// box. effectiveBox.height === pos.height for everything that doesn't auto-grow (images), so this is
	// behaviour-preserving there. Width isn't grown (effectiveBox.width is a dummy 0), so keep pos.width.
	const start: Box = { pos: effectiveBox.pos, dims: { width: storeBox.dims.width, height: effectiveBox.dims.height } };
	const hugFit  = impl.getHugFit();
	const bounds: ResizeBounds = { max: { width: canvas.width ?? Infinity, height: canvas.height ?? Infinity }, min: hugFit?.box.dims };
	const aspect  = isCornerHandle(handle) && impl.lockAspect();
	dragCtx.beginResize({ x: e.clientX, y: e.clientY },
		(dx, dy) => { impl.writeGeometry(aspect ? applyAspectResizeDelta(handle, start, dx, dy, bounds)
		                                        : applyResizeDelta(handle, start, dx, dy, bounds)) },
		() => {
			const after: Box = cloneBox(storeBox);
			// A press that never dragged gives back any detachment it took, so merely clicking a handle
			// can't quietly stop a leaf reflowing — and can't leave a detachment behind that no history
			// entry would ever undo. Same rule as a drag that never moved (see commitSingleMove).
			if (sameBox(start, after)) {
				if (detached) setDetachedInStore(objCtx, impl.id, null);
				return;
			}
			setObjectPositionSize(objCtx, impl.id, after.pos, after.dims);
			(commit?.history ?? histCtx).execute(resizeObjectCmd(objCtx, impl.id, start, after));
		},
	)
}

